import { Box, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StyledDataGrid from 'components/DataGridTable';
import { getItemStatus } from '../../utils/inventorySearch';
import { isWithoutMenuItem } from '../../utils/inventoryListQuery';

/** Grid column -> `sortBy` of the inventory list endpoint. */
export const SORT_BY_COLUMN = {
  name: 'name',
  categoryName: 'category',
  price: 'price',
  status: 'status',
  stock: 'stock',
  barcodeNumber: 'barcode',
  sku: 'sku',
  unit: 'unit',
};

const ItemsDataGrid = ({
  rows,
  rowCount,
  loading,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sortModel,
  onSortModelChange,
  onEdit,
  onDelete,
  onQuickView,
}) => {
  const columns = [
    {
      field: 'name',
      headerName: 'Item',
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
        >
          <Tooltip title="Click to quick edit" arrow>
            <Typography
              noWrap
              sx={{ cursor: 'pointer', color: 'black' }}
              onClick={(e) => onQuickView && onQuickView(e, params.row)}
            >
              {params.value}
            </Typography>
          </Tooltip>
          {isWithoutMenuItem(params.row) && (
            <Chip label="No menu item" size="small" variant="outlined" />
          )}
        </Box>
      ),
    },
    {
      field: 'categoryName',
      headerName: 'Category',
      width: 150,
      sortable: true,
      renderCell: (params) => {
        const category = params.row?.category;
        if (!category) return '';
        if (typeof category === 'object') {
          return <Typography variant="body2">{category.name || ''}</Typography>;
        }
        return <Typography variant="body2">{category}</Typography>;
      },
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 120,
      sortable: true,
      renderCell: (params) => {
        if (isWithoutMenuItem(params.row)) return '-';
        const price = params.row?.price || 0;
        return `$${Number(price).toFixed(2)}`;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      sortable: true,
      renderCell: (params) => {
        if (!params.row) return null;
        const status = getItemStatus(params.row);
        let backgroundColor = '#00984F14';
        let textColor = '#00984F';

        if (status === 'Out of Stock') {
          backgroundColor = '#E2150814';
          textColor = '#E21508';
        } else if (status === 'Low Stock') {
          backgroundColor = '#FF8A0014';
          textColor = '#FF8A00';
        }

        return (
          <Chip
            label={status}
            size="small"
            variant="filled"
            sx={{
              backgroundColor: backgroundColor,
              color: textColor,
              '& .MuiChip-label': {
                color: textColor,
              },
            }}
          />
        );
      },
    },
    {
      field: 'stock',
      headerName: 'Quantity',
      width: 120,
      sortable: true,
      renderCell: (params) => {
        const stock = params.row?.inventoryInfo?.stock ?? 0;
        return <Typography variant="body2">{stock}</Typography>;
      },
    },
    {
      field: 'barcodeNumber',
      headerName: 'Barcode',
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const barcodes = params.row?.barcodeNumbers || [];

        if (!barcodes || barcodes.length === 0) {
          return <Typography variant="body2">N/A</Typography>;
        }

        return (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              overflowX: 'auto',
              maxWidth: 300,
              '&::-webkit-scrollbar': {
                height: 1,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: 3,
              },
            }}
          >
            {barcodes.map((barcode, index) => (
              <Chip
                key={index}
                label={barcode}
                size="small"
                sx={{
                  flexShrink: 0,
                  fontSize: '14px',
                }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'sku',
      headerName: 'SKU',
      width: 200,
      sortable: true,
      renderCell: (params) => {
        const sku = params.row?.inventoryInfo?.sku || '';
        const displayText = sku || 'N/A';
        return (
          <Tooltip title={sku || 'N/A'} arrow>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {displayText}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'unit',
      headerName: 'Unit of Measure',
      width: 150,
      sortable: true,
      renderCell: (params) => {
        const unit = params.row?.inventoryInfo?.unit || '';
        const formattedUnit = unit
          ? unit.charAt(0).toUpperCase() + unit.slice(1)
          : 'N/A';
        return <Typography variant="body2">{formattedUnit}</Typography>;
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEdit && onEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => onDelete && onDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
      <StyledDataGrid
        rows={rows}
        rowCount={rowCount}
        columns={columns}
        getRowId={(row) => row._id}
        paginationMode="server"
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        rowsPerPageOptions={[25, 50, 100]}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        disableColumnFilter
        disableSelectionOnClick
        loading={loading}
        hideToolbar={true}
      />
    </Box>
  );
};

export default ItemsDataGrid;
